import { ButtonPrimary, ButtonSecondary } from "@nova/component-btn"
import { useCallback } from "react"
import {
    getGenericWorkspacePage,
    getGenericWorkspacePageHeader,
} from "panels/routing/WorkspaceRoutes"
import { SupportButton } from "panels/routing/SettingsRoutes"
import * as Icons from "@nova/component-icons"
import {
    PilotTrackingConnectionsProvider,
    usePilotTrackingConnectionsContext,
} from "features/data-management/pilot-tracking/PilotTrackingConnectionsContext"
import { IntegrationType } from "@nova/schemas"
import { IntegrationSection } from "./connected-accounts/integrations/module"
import { useIsEditor } from "tools/hooks/useIsEditor"
import { useNavigationState } from "@nova/react-router"
import { useConfirmationModalContext } from "features/modals/WorkspaceConfirmationModalV2"
import { ConnectedAccountSelectProviderModal } from "./connected-accounts/integrations/modals/ConnectedAccountSelectProviderModal"
import { NovaSpinner } from "@nova/component-nova-spinner"
import { useAppDispatch } from "integrations/redux/hooks"
import { setGlobalNotificationContext } from "user/profile/slice"
import { GlobalNotificationStatus } from "@nova/component-global-notification"

export const ConnectedAccountsSettingsPage = () => (
    <PilotTrackingConnectionsProvider>
        <ConnectedAccountsSettingsPageInner />
    </PilotTrackingConnectionsProvider>
)

const ConnectedAccountsSettingsPageInner = getGenericWorkspacePage({
    Header: getGenericWorkspacePageHeader({
        title: "Connected Accounts",
        rightItems: [SupportButton, AddAccountButton, SyncAllButton],
        border: true,
    }),
    Content: ConnectedAccountsSettingsContent,
})

function ConnectedAccountsSettingsContent() {
    const isEditor = useIsEditor()
    const { accountsByIntegrationType, hasConnectedAccount, isLoading } =
        usePilotTrackingConnectionsContext()

    if (isLoading) {
        return (
            <div className="w-full h-full flex justify-center items-center">
                <NovaSpinner width={40} height={40} />
            </div>
        )
    }

    if (!hasConnectedAccount) {
        return (
            <div className="flex flex-col items-center gap-2 pt-4">
                <h1 className="text-white">No Accounts Yet</h1>
                <h5 className="text-gray-100">Add an account to sync and receive your flights.</h5>
                {isEditor && <AddAccountButton />}
            </div>
        )
    }
    return (
        <div className="text-white flex flex-col pt-4">
            <h5 className="text-gray-100 mb-8">
                Connect accounts to automatically sync flight logs each week into this workspace.
            </h5>

            {!!accountsByIntegrationType &&
                (Object.keys(accountsByIntegrationType) as IntegrationType[]).map((type, i) => {
                    const accounts = accountsByIntegrationType[type]
                    return <IntegrationSection key={type} type={type} accounts={accounts} i={i} />
                })}
        </div>
    )
}

function AddAccountButton() {
    const isEditor = useIsEditor()
    const confirmationModal = useConfirmationModalContext()
    const {
        createNewDjiConnection,
        createNewAutelConnection,
        createNewSkydioConnection,
        hasConnectedAccount,
    } = usePilotTrackingConnectionsContext()

    const showSelectProviderModal = useCallback(() => {
        void confirmationModal.confirm({
            RenderComponent: (props) => (
                <ConnectedAccountSelectProviderModal
                    {...props}
                    onCreateDji={createNewDjiConnection}
                    onCreateAutel={createNewAutelConnection}
                    onCreateSkydio={createNewSkydioConnection}
                />
            ),
        })
    }, [
        confirmationModal,
        createNewDjiConnection,
        createNewAutelConnection,
        createNewSkydioConnection,
    ])

    useNavigationState((state) => {
        if (state === "openSelectProvider") showSelectProviderModal()
    })

    if (!isEditor) return null

    const ButtonComponent = hasConnectedAccount ? ButtonSecondary : ButtonPrimary

    return (
        <ButtonComponent
            title="Add Account"
            LeftIcon={Icons.Plus}
            onClick={showSelectProviderModal}
        />
    )
}

function SyncAllButton() {
    const isEditor = useIsEditor()
    const dispatch = useAppDispatch()
    const {
        accountsByIntegrationType,
        hasConnectedAccount,
        triggerDjiSync,
        triggerAutelSync,
        triggerSkydioSync,
    } = usePilotTrackingConnectionsContext()

    const syncAll = async () => {
        const triggerByType: Partial<Record<IntegrationType, (id: string) => Promise<void>>> = {
            [IntegrationType.dji]: triggerDjiSync,
            [IntegrationType.autel]: triggerAutelSync,
            [IntegrationType.skydio]: triggerSkydioSync,
        }
        try {
            await Promise.all(
                Object.values(accountsByIntegrationType)
                    .flat()
                    .map((a) => triggerByType[a.integrationType]?.(a.id))
            )
        } catch (err) {
            dispatch(
                setGlobalNotificationContext({
                    message: err instanceof Error ? err.message : "Failed to start sync",
                    status: GlobalNotificationStatus.error,
                    timeoutInMs: 5000,
                })
            )
        }
    }

    if (!isEditor || !hasConnectedAccount) return null
    return <ButtonPrimary title="Sync All" LeftIcon={Icons.Sync} onClick={syncAll} />
}
